import type { ReactNode, Ref, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { useFormFieldContext } from './form-field-context';
import styles from './Select.module.css';

export type SelectOption = {
  value: string;
  /** CEVRILMIS metin. Sabit listeler `labelKey` saklar, cagri yerinde t() ile cevrilir. */
  label: string;
  disabled?: boolean;
};

// `size` HTML'de sayisaldir (<select size="4">); kesisim `never` uretiyor.
export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'size'> & {
  ref?: Ref<HTMLSelectElement>;
  options: ReadonlyArray<SelectOption>;
  /** Bos secim metni ("Banka Seçin"). Verilirse listenin basina eklenir. */
  placeholder?: ReactNode;
  size?: 'sm' | 'md';
};

/**
 * Yerel <select>. Bilincli tercih: klavye, ekran okuyucu ve mobil tekerlek
 * davranisi bedava gelir; ozel bir acilir liste bunlarin hepsini yeniden
 * yazmayi gerektirir ve hicbiri bu fazda gerekmiyor.
 */
export function Select({
  options,
  placeholder,
  size = 'md',
  className,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  ...rest
}: SelectProps) {
  const field = useFormFieldContext();

  return (
    <div
      className={[
        styles.wrapper,
        styles[size],
        field?.invalid ? styles.invalid : undefined,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <select
        id={id ?? field?.controlId}
        aria-describedby={describedBy ?? field?.describedBy}
        aria-invalid={ariaInvalid ?? (field?.invalid ? true : undefined)}
        className={styles.select}
        {...rest}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className={styles.chevron} size={16} aria-hidden="true" />
    </div>
  );
}
